import { Injectable } from '@angular/core';
import { DataListModel } from '../../models/data-list-model';
import { Observable } from 'rxjs';
import { Product } from '../../dto/product';
import { HttpParams, HttpClient } from '@angular/common/http';
import { CrudService } from '../../services';
import { Upload } from '../../dto/upload';
import {CardAcceptorListDto, FeeGroup, LimitGroup, PermsGroup} from "../../dto/limitGroup";
import {Newsletter} from '../../dto/newsletter';
import {DateTableFilter} from "../../table-datasouces/date-table-filter";
import {PrevNextModel} from "../../models/prev-next-model";
import {toISODateString} from "../../providers/app-date-adapter";
import {PrevNextIdModel} from "../../models/prev-next-id-model";

@Injectable({
  providedIn: 'root'
})
export class ProductService implements CrudService<Product> {
  readonly apiPath = 'products/';
  constructor(
    private http: HttpClient,
  ) { }

  getMany(
    pageIndex: number,
    pageSize: number,
    sortField: string,
    sortDirection: string,
    filterString: string,
  ): Observable<DataListModel<Product>> {
    const params = new HttpParams()
      .set('page',          pageIndex.toString())
      .set('pageSize',      pageSize.toString())
      .set('filterString',  filterString)
      .set('sortField',     sortField)
      .set('sortDirection', sortDirection);
    return this.http.get(this.apiPath, { params }) as Observable<DataListModel<Product>>;
  }

  addImage( item: File ): Observable<Upload> {
    const data = new FormData();
    data.append( 'image', item, item.name );
    return this.http.post( this.apiPath + 'image', data ) as Observable<Upload>;
  }

  getAll(): Observable<DataListModel<Product>> {
    const params = new HttpParams()
      .set('page', 1)
      .set('pageSize', 1000)
    ;

    return this.http.get(this.apiPath, {params}) as Observable<DataListModel<Product>>;
  }

  getLimitGroups(): Observable<LimitGroup[]> {
    return this.http.get(this.apiPath + 'limit-groups') as Observable<LimitGroup[]>;
  }

  getPermsGroups(): Observable<PermsGroup[]> {
    return this.http.get(this.apiPath + 'perms-groups') as Observable<PermsGroup[]>;
  }

  getActiveCardsCount(): Observable<number> {
    return this.http.get(this.apiPath + 'active-cards-count') as Observable<number>;
  }

  getFeeGroups(): Observable<FeeGroup[]> {
    return this.http.get(this.apiPath + 'fee-groups') as Observable<FeeGroup[]>;
  }

  getWhiteLists(): Observable<CardAcceptorListDto[]> {
    return this.http.get(this.apiPath + 'white-lists') as Observable<CardAcceptorListDto[]>;
  }
  getBlackLists(): Observable<CardAcceptorListDto[]> {
    return this.http.get(this.apiPath + 'black-lists') as Observable<CardAcceptorListDto[]>;
  }

  getAllWithCompanyIdInclArchived(): Observable<DataListModel<Product>> {
    return this.http.get(this.apiPath + 'get-all-with-company-id-incl-archived') as Observable<DataListModel<Product>>;
  }

  get(id: number): Observable<Product> {
    return this.http.get(this.apiPath + id) as Observable<Product>;
  }

  add(item: Product): Observable<Object> {
    return this.http.post(this.apiPath, item, {headers:{'skip-error-snack':'true'} });
  }

  update(id: number, item: Product): Observable<Object> {
    return this.http.put(this.apiPath + id, item, {headers:{'skip-error-snack':'true'} });
  }

  delete(id: any) {
    return this.deleteMany([id]);
  }

  restore(id: any) {
    return this.restoreMany([id]);
  }

  deleteMany(idList: any[]): Observable<Object> {
    let httpParams = new HttpParams();

    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });

    return this.http.delete(this.apiPath, { params: httpParams });
  }
  restoreMany(idList: any[]): Observable<Object> {
    let httpParams = new HttpParams();
    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });
    return this.http.patch(this.apiPath, null, { params: httpParams });
  }

  copy(id: number) {
    return this.http.post(this.apiPath + 'copy', {id}) as Observable<Product>;
  }

  getForCompany(companyId: number, includeInactive: boolean = false) {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('include_inactive', includeInactive ? 'true' : 'false');
    return this.http.get(this.apiPath + 'get-for-company/' + companyId, {params: httpParams}) as Observable<Product[]>;
  }

  getForCompanies(companyIds: number[], includeInactive: boolean = false) {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('include_inactive', includeInactive ? 'true' : 'false');
    httpParams = httpParams.append('companyIds', companyIds.join(','));

    return this.http.get(this.apiPath + 'get-for-companies', {params: httpParams}) as Observable<Product[]>;
  }

  getLogoFilename(productId: number) : Observable<string> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('productId', productId);
    return this.http.get(this.apiPath + 'logo-filename', {params: httpParams}) as Observable<string>;
  }

  getCleanCardImageFilename(productId: number) : Observable<string> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('productId', productId);
    return this.http.get(this.apiPath + 'clean-card-image-filename', {params: httpParams}) as Observable<string>;
  }

  getCombinedImageUrl(productId: number) : Observable<string> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('productId', productId);
    return this.http.get(this.apiPath + 'combined-image-url', {params: httpParams}) as Observable<string>;
  }

  getPrevNext(filters: DateTableFilter, id: number): Observable<PrevNextModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${id}/prev-next`, { params }) as Observable<PrevNextModel>;
  }

  getFilterParams(filters: DateTableFilter): HttpParams {
    const str =  filters != null ? filters.str || '' : '';
    let params = new HttpParams()
      .set('filterString', str)
    ;

    if (filters != null && filters.sortField) {
      params = params.set('sortField', filters.sortField);
    }
    if (filters != null && filters.sortDirection) {
      params = params.set('sortDirection', filters.sortDirection);
    }


    return params;
  }

  getPrevNextIds(filters: DateTableFilter, id: number): Observable<PrevNextIdModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${id}/prev-next-ids`, { params }) as Observable<PrevNextIdModel>;
  }

}
